import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import CountUp from 'react-countup'
import { FaChevronDown } from 'react-icons/fa'
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'
import AuthContext from 'context/AuthContext'
import { apiGet } from 'utils/apiFetch'
import apiPath from 'utils/apiPath'

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const BAR_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
}
const DOUGHNUT_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: { legend: { display: false } }
}
const STATUS_PALETTE = ['#4BC0C0', '#FF9F40', '#FFCE56', '#36A2EB', '#9966FF', '#FF6384', '#4C6FFF', '#C9CBCF']

function statusClass (status) {
  const value = String(status || '').toLowerCase()
  if (['active', 'complete', 'signed', 'clear', 'pass'].includes(value)) {
    return 'bg-green-100 text-green-800'
  }
  if (['pending', 'in progress', 'analyzing', 'waiting on candidate', 'action required'].includes(value)) {
    return 'bg-amber-100 text-amber-800'
  }
  if (['expired', 'inactive', 'cancelled', 'in dispute', 'fail', 'hits'].includes(value)) {
    return 'bg-red-100 text-red-800'
  }
  return 'bg-gray-100 text-gray-700'
}

function StatusBadge ({ value }) {
  if (!value) return <span>-</span>
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs capitalize ${statusClass(value)}`}>
      {value}
    </span>
  )
}

function checkResult (check) {
  if (typeof check?.resultStatus === 'string' && check.resultStatus) return check.resultStatus
  const data = check?.resultData
  if (Array.isArray(data) && data.length) {
    if (data.some((scan) => scan.score === 'FAIL')) return 'Fail'
    if (data.every((scan) => scan.score === 'PASS')) return 'Pass'
    if (data.some((scan) => scan.score === 'PASS')) return 'Partial'
  }
  return 'Pending'
}

function candidateName (check) {
  const first = check?.employeeData?.firstName || ''
  const last = check?.employeeData?.lastName || ''
  const combined = `${first} ${last}`.trim()
  return combined || check?.employeeID?.name || '-'
}

function AccordionRow ({ title, detail, open, onToggle, children }) {
  return (
    <div className='bg-white border border-[#eceef5] rounded-xl mb-3'>
      <button type='button' onClick={onToggle} className='w-full flex items-center justify-between gap-4 px-5 py-4 text-left'>
        <div>
          <div className='font-semibold text-slate-900'>{title}</div>
          <div className='text-sm text-gray-500 mt-1'>{detail}</div>
        </div>
        <FaChevronDown className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? <div className='px-3 pb-3'>{children}</div> : null}
    </div>
  )
}

function SectionTable ({ headers, rows, renderRow, empty }) {
  return (
    <div className='bg-white rounded-lg border'>
      <div className='overflow-x-auto'>
        <table className='w-full text-xs text-left text-[#6c6c6c]'>
          <thead className='border border-[#E1E6EE] bg-[#E1E6EE] text-xs text-gray-900'>
            <tr>
              {headers.map((header) => (
                <th key={header} className='py-3 px-4' scope='col'>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map(renderRow)
            ) : (
              <tr>
                <td className='py-6 px-4 text-center text-gray-500' colSpan={headers.length}>
                  {empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Credentialing () {
  const { updatePageName } = useContext(AuthContext)
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [checks, setChecks] = useState([])
  const [checkTotal, setCheckTotal] = useState(0)
  const [categories, setCategories] = useState([])
  const [employeeDocuments, setEmployeeDocuments] = useState([])
  const [openIds, setOpenIds] = useState([])

  useEffect(() => {
    updatePageName(t('CREDENTIALING_BOARD'))
  }, [])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const [checksRes, categoryRes, submittedRes] = await Promise.allSettled([
        apiGet(apiPath.backgroundCheck, { page: 1, limit: 10, sortBy: 'orderedAt', sortType: 'desc' }),
        apiGet(apiPath.categoryList, { page: 1, pageSize: 100, sortBy: 'createdAt', sortType: 'desc' }),
        apiGet(apiPath.submitFormByEmployee, { page: 1, pageSize: 20, sortBy: 'createdAt', sortType: 'desc' })
      ])

      if (checksRes.status === 'fulfilled' && checksRes.value?.data?.success) {
        const results = checksRes.value.data.results || {}
        setChecks(results.checks || [])
        setCheckTotal(results.pagination?.total || results.checks?.length || 0)
      } else {
        setChecks([])
        setCheckTotal(0)
      }

      if (categoryRes.status === 'fulfilled' && categoryRes.value?.data?.success) {
        setCategories(categoryRes.value.data.results?.docs || [])
      } else {
        setCategories([])
      }

      if (submittedRes.status === 'fulfilled' && submittedRes.value?.data?.success) {
        const forms = submittedRes.value.data.results?.finalData?.docs || []
        setEmployeeDocuments(
          forms.flatMap((item) =>
            (item.EmployeeDocuments || []).map((doc) => ({
              ...doc,
              employeeName: item?.Employee?.name,
              employeeCode: item?.Employee?.employeeId
            }))
          )
        )
      } else {
        setEmployeeDocuments([])
      }

      setLoading(false)
    }
    load()
  }, [])

  const expiryCategories = useMemo(
    () => categories.filter((item) => item.hasMandatoryExpiry === 'yes'),
    [categories]
  )
  const expiryRequired = expiryCategories.length
  const withExpiry = useMemo(
    () => employeeDocuments.filter((item) => item.expiryDate),
    [employeeDocuments]
  )
  const statusChart = useMemo(() => {
    const groups = {}
    checks.forEach((item) => {
      const label = item.status || 'Unknown'
      const key = String(label).toLowerCase()
      if (!groups[key]) groups[key] = { label, count: 0 }
      groups[key].count += 1
    })
    const statusGroups = Object.values(groups)
    return {
      labels: statusGroups.length ? statusGroups.map((group) => group.label) : ['No checks'],
      datasets: [{
        data: statusGroups.length ? statusGroups.map((group) => group.count) : [1],
        backgroundColor: statusGroups.length
          ? statusGroups.map((_, index) => STATUS_PALETTE[index % STATUS_PALETTE.length])
          : ['#E5E7EB'],
        borderWidth: 0
      }]
    }
  }, [checks])

  const cards = [
    { id: 'checks', label: 'Background checks', value: checkTotal, to: '/background-verification' },
    { id: 'categories', label: 'Categories requiring expiry', value: expiryRequired, to: '/document-manager', tab: 'categories', onlyExpiry: true },
  ]
  const toggleSection = (id) => {
    setOpenIds((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ))
  }
  const overviewChart = useMemo(() => ({
    labels: ['Background checks', 'Categories requiring expiry'],
    datasets: [{
      label: 'Total',
      data: [checkTotal, expiryRequired, withExpiry.length],
      backgroundColor: ['#4C6FFF', '#7C5CFC', '#FF9F40', '#4BC0C0'],
      borderRadius: 6,
      maxBarThickness: 42
    }]
  }), [checkTotal, expiryRequired, withExpiry.length])

  return (
    <div className='md:px-8 py-4 px-4'>
      <div className='grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6'>
        {cards.map((card) => (
          <Link
            key={card.id}
            to={card.to}
            state={{ tab: card.tab, onlyExpiry: !!card.onlyExpiry }}
            onClick={() => {
              if (card.tab) sessionStorage.setItem('document_manager_active_tab', card.tab)
              if (card.onlyExpiry) sessionStorage.setItem('document_manager_only_expiry', 'yes')
              updatePageName(card.label)
            }}
            className='border rounded-lg px-4 py-5 text-left w-full bg-white hover:bg-blue-50'
          >
            <h3 className='font-bold text-2xl text-slate-900 mb-1'>
              {loading ? '—' : <CountUp end={card.value} duration={0.6} />}
            </h3>
            <span className='text-sm text-gray-700'>{card.label}</span>
          </Link>
        ))}
      </div>
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6'>
        <div className='bg-white p-4 rounded-lg border'>
          <h3 className='text-base font-semibold mb-4 text-slate-900'>Credentialing overview</h3>
          <div className='h-64 overflow-hidden'>
            <Bar data={overviewChart} options={BAR_OPTIONS} />
          </div>
        </div>
        <div className='bg-white p-4 rounded-lg border'>
          <h3 className='text-base font-semibold mb-4 text-slate-900'>Background check status</h3>
          <div className='h-64 overflow-hidden'>
            <Doughnut data={statusChart} options={DOUGHNUT_OPTIONS} />
          </div>
        </div>
      </div>


      <AccordionRow
        title='Background checks'
        detail={loading ? '—' : `${checkTotal} ${checkTotal === 1 ? 'check' : 'checks'}`}
        open={openIds.includes('checks')}
        onToggle={() => toggleSection('checks')}
      >
      <SectionTable
        headers={['Candidate', 'Email', 'Status', 'Result', 'Ordered']}
        rows={checks}
        empty={loading ? 'Loading background checks…' : 'No background checks yet.'}
        renderRow={(item) => (
          <tr key={item._id} className='bg-white border-b'>
            <td className='py-3 px-4 text-gray-900'>{candidateName(item)}</td>
            <td className='py-3 px-4'>{item?.employeeData?.email || item?.employeeID?.email || '-'}</td>
            <td className='py-3 px-4'><StatusBadge value={item.status} /></td>
            <td className='py-3 px-4'><StatusBadge value={checkResult(item)} /></td>
            <td className='py-3 px-4'>{item.orderedAt ? dayjs(item.orderedAt).format('MM-DD-YYYY h:mm A') : '-'}</td>
          </tr>
        )}
      />
      </AccordionRow>

      <AccordionRow
        title='Categories requiring expiry'
        detail={loading ? '—' : `${expiryRequired} ${expiryRequired === 1 ? 'category' : 'categories'}`}
        open={openIds.includes('categories')}
        onToggle={() => toggleSection('categories')}
      >
      <SectionTable
        headers={['Category', 'Mandatory expiry', 'Signature required', 'Status']}
        rows={expiryCategories}
        empty={loading ? 'Loading categories…' : 'No document categories yet.'}
        renderRow={(item) => (
          <tr key={item._id} className='bg-white border-b'>
            <td className='py-3 px-4 text-gray-900'>{item.name || '-'}</td>
            <td className='py-3 px-4 capitalize'>{item.hasMandatoryExpiry || '-'}</td>
            <td className='py-3 px-4 capitalize'>{item.requiresElectronicSignature || '-'}</td>
            <td className='py-3 px-4'><StatusBadge value={item.status} /></td>
          </tr>
        )}
      />
      </AccordionRow>

  
    </div>
  )
}

export default Credentialing